import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getDepreciationHistory } from '../../services'
import { DepreciationHistory as DepreciationHistoryType } from '../../types'
import { Badge } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Calculator, Edit2, Percent, ArrowDown } from 'lucide-react'

interface DepreciationHistoryProps {
    itemId: string
}

/**
 * DepreciationHistory - Displays timeline of depreciation changes
 * Shows action type, value changes, who made the change, and reason
 */
const DepreciationHistory = ({ itemId }: DepreciationHistoryProps) => {
    const { data: history, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_ITEMS, itemId, 'depreciation-history'],
        queryFn: () => getDepreciationHistory(itemId),
    })

    // Format currency
    const formatCurrency = (value?: number) => {
        if (value === undefined || value === null) return 'N/A'
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
            maximumFractionDigits: 0,
        }).format(value)
    }

    // Format date
    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    // Get action icon
    const getActionIcon = (action: string) => {
        switch (action) {
            case 'calculate':
                return <Calculator className="w-4 h-4" />
            case 'manual_adjust':
                return <Edit2 className="w-4 h-4" />
            case 'rate_change':
                return <Percent className="w-4 h-4" />
            default:
                return <ArrowDown className="w-4 h-4" />
        }
    }

    // Get action label
    const getActionLabel = (action: string) => {
        switch (action) {
            case 'calculate':
                return 'Calculated'
            case 'manual_adjust':
                return 'Manual Adjust'
            case 'rate_change':
                return 'Rate Changed'
            default:
                return action
        }
    }

    // Get action badge variant
    const getActionBadgeVariant = (
        action: string
    ): 'default' | 'secondary' | 'outline' => {
        switch (action) {
            case 'calculate':
                return 'default'
            case 'manual_adjust':
                return 'secondary'
            case 'rate_change':
                return 'outline'
            default:
                return 'outline'
        }
    }

    // Render loading/error state
    if (renderState()) return renderState()

    if (!history || history.length === 0) {
        return (
            <p className="text-sm text-muted-foreground text-center py-4">
                No depreciation history found
            </p>
        )
    }

    return (
        <div className="rounded-md border max-h-96 overflow-auto">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Action</TableHead>
                        <TableHead>Previous</TableHead>
                        <TableHead>New</TableHead>
                        <TableHead>Rate</TableHead>
                        <TableHead>Months</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Date</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {history.map((entry: DepreciationHistoryType) => {
                        const change = entry.new_value - entry.previous_value
                        return (
                            <TableRow key={entry.id}>
                                <TableCell>
                                    <Badge
                                        variant={getActionBadgeVariant(
                                            entry.action
                                        )}
                                        className="flex items-center gap-1 w-fit"
                                    >
                                        {getActionIcon(entry.action)}
                                        {getActionLabel(entry.action)}
                                    </Badge>
                                </TableCell>
                                <TableCell className="font-mono text-sm">
                                    {formatCurrency(entry.previous_value)}
                                </TableCell>
                                <TableCell className="font-mono text-sm">
                                    <span
                                        className={
                                            change < 0
                                                ? 'text-red-600'
                                                : 'text-green-600'
                                        }
                                    >
                                        {formatCurrency(entry.new_value)}
                                    </span>
                                </TableCell>
                                <TableCell className="text-sm">
                                    {entry.depreciation_rate}%
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                    {entry.months_applied > 0
                                        ? `${entry.months_applied}m`
                                        : '-'}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground max-w-32 truncate">
                                    {entry.reason || '-'}
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                    {formatDate(entry.performed_at)}
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </div>
    )
}

export default DepreciationHistory
